import React, { useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TypingAnimation from './TypingAnimation';

// Neural Network Background Component
const NeuralNetworkBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Neural network nodes
    const nodes: Array<{ x: number; y: number; vx: number; vy: number; connections: number[] }> = [];
    const nodeCount = 50;

    // Initialize nodes
    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        connections: []
      });
    }

    // Create connections
    nodes.forEach((node, i) => {
      const connectionCount = Math.floor(Math.random() * 3) + 1;
      for (let j = 0; j < connectionCount; j++) {
        const targetIndex = Math.floor(Math.random() * nodeCount);
        if (targetIndex !== i && !node.connections.includes(targetIndex)) {
          node.connections.push(targetIndex);
        }
      }
    });

    let animationId: number;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update node positions
      nodes.forEach(node => {
        node.x += node.vx;
        node.y += node.vy;

        // Bounce off edges
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

        // Keep nodes in bounds
        node.x = Math.max(0, Math.min(canvas.width, node.x));
        node.y = Math.max(0, Math.min(canvas.height, node.y));
      });

      // Draw connections
      nodes.forEach((node, i) => {
        node.connections.forEach(targetIndex => {
          const target = nodes[targetIndex];
          const distance = Math.sqrt(
            Math.pow(node.x - target.x, 2) + Math.pow(node.y - target.y, 2)
          );

          if (distance < 200) {
            const opacity = 1 - (distance / 200);
            ctx.strokeStyle = `rgba(59, 130, 246, ${opacity * 0.3})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(target.x, target.y);
            ctx.stroke();
          }
        });
      });

      // Draw nodes
      nodes.forEach(node => {
        ctx.fillStyle = theme.palette.primary.main;
        ctx.shadowColor = theme.palette.primary.main;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(node.x, node.y, 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, [theme.palette.primary.main]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        opacity: 0.6,
      }}
    />
  );
};

// Animated Gradient Background
const AnimatedGradient: React.FC = () => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(0, 255, 136, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.05) 100%)
        `,
        zIndex: 0,
      }}
    />
  );
};

// Floating Particles
const FloatingParticles: React.FC = () => {
  const blueColors = ['#3b82f6', '#60a5fa', '#93c5fd', '#8b5cf6', '#a78bfa', '#c4b5fd'];
  
  return (
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }}>
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            background: blueColors[i % blueColors.length],
            boxShadow: `0 0 10px ${blueColors[i % blueColors.length]}`,
          }}
          animate={{
            x: [0, Math.random() * 100 - 50],
            y: [0, Math.random() * 100 - 50],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
          initial={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
          }}
        />
      ))}
    </Box>
  );
};

const Hero: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" as const },
    },
  };

  return (
    <Box
      component="section"
      sx={{
        minHeight: { xs: '70vh', sm: '80vh', md: '85vh', lg: '90vh' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 6, sm: 8, md: 10 },
        pt: { xs: '80px', sm: '90px', md: '100px' },
        background: `linear-gradient(135deg, 
          rgba(59, 130, 246, 0.05) 0%, 
          rgba(139, 92, 246, 0.05) 50%, 
          rgba(0, 255, 136, 0.05) 100%)`,
      }}
    >
      {/* Background Layers */}
      <AnimatedGradient />
      <NeuralNetworkBackground />
      <FloatingParticles />

      {/* Glow Effects */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.primary.main}20 0%, transparent 70%)`,
          filter: 'blur(60px)',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '5%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.secondary.main}20 0%, transparent 70%)`,
          filter: 'blur(50px)',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Main Content */}
          <Box
            sx={{
              textAlign: 'center',
              maxWidth: '900px',
              mx: 'auto',
            }}
          >
            {/* Logo */}
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: { xs: 2, sm: 3, md: 4 },
                }}
              >
                <Box
                  component="img"
                  src="/knoowl-logo.png"
                  alt="Knoowl Logo"
                  sx={{
                    height: { xs: 50, sm: 60, md: 80, lg: 100 },
                    width: 'auto',
                    filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none',
                  }}
                />
              </Box>
            </motion.div>

            {/* Subtitle */}
            <motion.div variants={itemVariants}>
              <Typography
                variant={isSmallMobile ? 'h5' : isMobile ? 'h4' : 'h3'}
                sx={{
                  fontWeight: 400,
                  mb: { xs: 1.5, sm: 2, md: 2 },
                  color: 'text.primary',
                  fontFamily: '"Charter", "Georgia", serif',
                  px: { xs: 2, md: 0 },
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: `0 0 20px ${theme.palette.primary.main}40`,
                }}
              >
                AI guides you. Blockchain pays you.
              </Typography>
            </motion.div>

            {/* Typing Animation */}
            <motion.div variants={itemVariants}>
              <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
                <TypingAnimation />
              </Box>
            </motion.div>

            {/* Additional Value Proposition */}
            <motion.div variants={itemVariants}>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mb: { xs: 2.5, sm: 3, md: 4 },
                  maxWidth: '700px',
                  mx: 'auto',
                  lineHeight: 1.6,
                  opacity: 0.9,
                  fontFamily: '"Kievit", "Roboto", sans-serif',
                  px: { xs: 2, md: 0 },
                  fontSize: { xs: '0.9rem', md: '1rem' },
                }}
              >
                Perfect for students preparing for exams, teachers creating engaging content, and parents supporting their children's education. Our AI adapts to your unique learning patterns.
              </Typography>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, md: 2 }}
                justifyContent="center"
                sx={{ mb: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, md: 0 } }}
              >
                <Button
                  variant="contained"
                  size={isSmallMobile ? 'medium' : 'large'}
                  href="https://www.knoowl.in/dashboard"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.2, md: 1.5 },
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    fontWeight: 500,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 4px 20px ${theme.palette.primary.main}40`,
                    fontFamily: '"Kievit", "Roboto", sans-serif',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)`,
                      transition: 'left 0.5s',
                    },
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 25px ${theme.palette.primary.main}50`,
                      '&::before': {
                        left: '100%',
                      },
                    },
                  }}
                >
                  Try Beta
                </Button>
                <Button
                  variant="outlined"
                  size={isSmallMobile ? 'medium' : 'large'}
                  startIcon={<PlayArrowIcon />}
                  sx={{
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.2, md: 1.5 },
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    fontWeight: 500,
                    borderRadius: 2,
                    borderColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.main,
                    fontFamily: '"Kievit", "Roboto", sans-serif',
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}10 0%, ${theme.palette.secondary.main}05 100%)`,
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: theme.palette.secondary.dark,
                      backgroundColor: `${theme.palette.secondary.main}20`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 25px ${theme.palette.secondary.main}30`,
                    },
                  }}
                >
                  See How It Works
                </Button>
              </Stack>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Hero; 